import { zodResolver } from "@hookform/resolvers/zod"
import { useRouter } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { CardDescription, CardTitle } from "design-system/molecules/Card"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useInviteMutation } from "modules/auth/hooks/useInviteMutation"
import { useAuth } from "modules/shared/hooks/useAuth"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { SignupInvitationSchema } from "schema"
import { z } from "zod"

type FieldValues = z.infer<typeof SignupInvitationSchema>

type SignupWithTokenFormProps = {
  token: string
  senderName: string
  name: string
  email: string
}

export function SignupWithTokenForm({ token, senderName, name, email }: SignupWithTokenFormProps) {
  const { mutateAsync, isPending } = useInviteMutation()
  const router = useRouter()
  const { onLogin } = useAuth()

  const onSubmit = async (data: z.infer<typeof SignupInvitationSchema>) => {
    const response = await mutateAsync({ ...data, token })
    onLogin(response, router)
  }

  const form = useForm<FieldValues>({
    resolver: zodResolver(SignupInvitationSchema),
    defaultValues: {
      token,
      name: name,
      password: "",
    },
  })

  const { t } = useTranslation()

  return (
    <Form
      {...form}
      errorTranslations={getErrorTranslations()}
      onSubmit={form.handleSubmit(onSubmit)}
      className="gap-6"
    >
      <div className="flex flex-col gap-2 mb-4">
        <CardTitle>{t("auth.signupWithToken.title")}</CardTitle>
        <CardDescription>{t("auth.signupWithToken.description", { senderName })}</CardDescription>
      </div>
      <div className="space-y-2 flex flex-col gap-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("auth.name.field")}</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder={t("auth.name.placeholder")}
                  {...field}
                  className="bg-background/75"
                />
              </FormControl>
              <FormDescription />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormItem>
          <FormLabel>{t("auth.email.field")}</FormLabel>
          <Input type="email" disabled value={email} className="bg-background/75" />
          <FormDescription />
          <FormMessage />
        </FormItem>
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("auth.signupWithToken.password.field")}</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder={t("auth.password.placeholder")}
                  autoComplete="new-password" // Prevent this to be already filled
                  {...field}
                  className="bg-background/75"
                />
              </FormControl>
              <FormDescription>{t("auth.signupWithToken.password.description")}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <Button disabled={isPending}>{t("auth.signupWithToken.submit")}</Button>
    </Form>
  )
}
